import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { urlMapper } from '@shared/models';
import { WindowRef } from '@shared/services/window-ref/window-ref.service';
import {
  selectInheritedIraContactInfoNextPage,
  selectInheritedIraDecedentInfoNextPage,
  selectInheritedIraEmploymentNextPage,
  selectInheritedIraIncomeNetWorthNextPage,
  selectInheritedIraInvestmentObjNextPage,
  selectInheritedIraLetterUploadNextPage,
  selectInheritedIraPersonalInfoNextPage,
  selectInheritedIraReviewNextPage,
  selectInheritedIraSettlementFundNextPage,
} from '@store/selectors/router/inherited-ira/inherited-ira-router.selectors';
import { exhaustMap, map, of, tap, throttleTime, withLatestFrom } from 'rxjs';

import * as RouterActions from '../../actions/router/router.actions';
import * as fromRootStore from '../../index';
import { RouterStateUrl } from '../../index';
import {
  selectRouterState,
  selectTestMonkeyNextPage,
} from '../../selectors/router/common/router.selectors';
import {
  selectGiftingAssetsNextPage,
  selectGiftingMethodNextPage,
  selectGiftingOverviewNextPage,
  selectGiftingRecipientNextPage,
  selectGiftingRecipientTriageNextPage,
  selectGiftingReviewNextPage,
  selectGiftingTermsNextPage,
  selectGiftingTriageNextPage,
} from '../../selectors/router/gifting/gifting-router.selectors';
import {
  selectInheritanceAssumeNextPage,
  selectInheritanceBeneDetailsNextPage,
  selectInheritanceBeneRelationshipNextPage,
  selectInheritanceContactInfoNextPage,
  selectInheritanceDecedentInfoNextPage,
  selectInheritanceEmploymentNextPage,
  selectInheritanceEstateNextPage,
  selectInheritanceIncomeNetWorthNextPage,
  selectInheritanceInvestObjNextPage,
  selectInheritanceIraTypeNextPage,
  selectInheritanceLetterUploadNextPage,
  selectInheritanceOverviewNextPage,
  selectInheritancePersonalInfoNextPage,
  selectInheritanceReviewNextPage,
  selectInheritanceTCFTrustInfoNextPage,
  selectInheritanceTCFTrustSignNextPage,
  selectInheritanceTermsNextPage,
  selectInheritanceTransferFromNextPage,
  selectInheritanceTransferToNextPage,
  selectInheritanceTriageNextPage,
  selectInheritanceTrustInfoNextPage,
  selectInheritanceTrustTaxInfoNextPage,
  selectInheritanceUploadNextPage,
} from '../../selectors/router/inheritance/inheritance-router.selectors';
import {
  selectDivorceDocUploadNextPage,
  selectDivorceReviewHoldingsNextPage,
  selectDivorceReviewNextPage,
  selectDivorceTermsNextPage,
  selectInternalTransferContactInfoNextPage,
  selectInternalTransferCostBasisOptInNextPage,
  selectInternalTransferDetailsNextPage,
  selectInternalTransferDifferentOwnersNextPage,
  selectInternalTransferEmploymentNextPage,
  selectInternalTransferFromNextPage,
  selectInternalTransferHoldingsReviewNextPage,
  selectInternalTransferIncomeNetWorthNextPage,
  selectInternalTransferInvestObjNextPage,
  selectInternalTransferOpenAccountNextPage,
  selectInternalTransferOverviewNextPage,
  selectInternalTransferPersonalInfoNextPage,
  selectInternalTransferReviewNextPage,
  selectInternalTransferSettlementFundNextPage,
  selectInternalTransferTermsExistingNextPage,
  selectInternalTransferToNextPage,
  selectInternalTransferTriageNextPage,
} from '../../selectors/router/internal-transfer/internal-transfer-router.selectors';
import {
  selectMinorContactInfoNextPage,
  selectMinorEmploymentNextPage,
  selectMinorIncomeNetWorthNextPage,
  selectMinorInvestObjNextPage,
  selectMinorPersonalInfoNextPage,
  selectMinorReviewNextPage,
  selectMinorSettlementFundNextPage,
  selectMinorTermsNextPage,
  selectMinorTransferFromNextPage,
  selectMinorTransferToNextPage,
} from '../../selectors/router/minor-transfer/minor-transfer-router.selectors';
import {
  selectMultiInheritanceAccountTriageNextPage,
  selectMultiInheritanceContactInfoNextPage,
  selectMultiInheritanceDecedentInfoNextPage,
  selectMultiInheritanceEmploymentNextPage,
  selectMultiInheritanceIncomeNetWorthNextPage,
  selectMultiInheritanceInvestObjNextPage,
  selectMultiInheritanceLetterUploadNexPage,
  selectMultiInheritanceNewAccountReviewNextPage,
  selectMultiInheritancePersonalInfoNextPage,
  selectMultiInheritanceReviewNextPage,
  selectMultiInheritanceTermsNextPage,
  selectMultiInheritanceTriageNextPage,
  selectMultiInheritanceTrustTriageNextPage,
  selectMultiInheritanceUploadNextPage,
} from '../../selectors/router/multi-inheritance/multi-inheritance-router.selectors';
import {
  selectDAEnrollNextPage,
  selectDAOfferNextPage,
  selectInvestmentStrategyNextPage,
  selectMultiAccountEnrollmentNextPage,
  selectPortfolioMatchNextPage,
  selectRiskQuizResultsNextPage,
  selectRiskQuizSetupNextPage,
  selectTaxFilingRetirementNextPage,
} from '../../selectors/router/open-choice/open-choice-router.selectors';
import { selectTriageState } from '../../selectors/triage/triage.selectors';

@Injectable()
export class RouterEffects {
  constructor(
    private actions$: Actions,
    private router: Router,
    private location: Location,
    private globalStore: Store<fromRootStore.State>,
    private winRef: WindowRef,
  ) {}

  routeMap = {
    '/secure/testmonkey': selectTestMonkeyNextPage,
    inheritance: {
      // inheritance public pages
      guide: selectInheritanceOverviewNextPage, // not necessary but leaving now
      overview: selectInheritanceOverviewNextPage, // setting us up to change on public site
      triage: selectInheritanceTriageNextPage,
      'trust-triage': selectInheritanceTriageNextPage,
      // inheritance secure pages
      'decedent-info': selectInheritanceDecedentInfoNextPage,
      upload: selectInheritanceUploadNextPage,
      assume: selectInheritanceAssumeNextPage,
      'transfer-from': selectInheritanceTransferFromNextPage,
      'transfer-to': selectInheritanceTransferToNextPage,
      'ira-type': selectInheritanceIraTypeNextPage,
      relationship: selectInheritanceBeneRelationshipNextPage,
      details: selectInheritanceBeneDetailsNextPage,
      estate: selectInheritanceEstateNextPage,
      'personal-info': selectInheritancePersonalInfoNextPage,
      employment: selectInheritanceEmploymentNextPage,
      letter: selectInheritanceLetterUploadNextPage,
      'contact-info': selectInheritanceContactInfoNextPage,
      'investment-objectives': selectInheritanceInvestObjNextPage,
      'income-net-worth': selectInheritanceIncomeNetWorthNextPage,
      review: selectInheritanceReviewNextPage,
      existing: selectInheritanceTermsNextPage,
      new: selectInheritanceTermsNextPage,
      'trust-info': selectInheritanceTrustInfoNextPage,
      'trust-tax-info': selectInheritanceTrustTaxInfoNextPage,
      'tcf-info-trust': selectInheritanceTCFTrustInfoNextPage,
      'tcf-sign-trust': selectInheritanceTCFTrustSignNextPage,
    },
    gifting: {
      // gifting public pages
      overview: selectGiftingOverviewNextPage, // setting us up to change on public site
      'gifting-welcome': selectGiftingOverviewNextPage,
      triage: selectGiftingTriageNextPage,
      // gifting secure pages
      assets: selectGiftingAssetsNextPage,
      method: selectGiftingMethodNextPage,
      recipient: selectGiftingRecipientNextPage,
      'recipient-triage': selectGiftingRecipientTriageNextPage,
      review: selectGiftingReviewNextPage,
      'review-multisigner': selectGiftingReviewNextPage,
      'gift-existing': selectGiftingTermsNextPage,
      'gift-existing-multisigner': selectGiftingTermsNextPage,
      // summary and method have their own effects for navigation
    },
    'internal-transfer': {
      overview: selectInternalTransferOverviewNextPage,
      triage: selectInternalTransferTriageNextPage,
      //internal transfer secure
      'transfer-details': selectInternalTransferDetailsNextPage,
      'transfer-to': selectInternalTransferToNextPage,
      'transfer-from': selectInternalTransferFromNextPage,
      'holdings-review': selectInternalTransferHoldingsReviewNextPage,
      'holdings-review-multisigner': selectInternalTransferHoldingsReviewNextPage,
      'different-owner-recipient': selectInternalTransferDifferentOwnersNextPage,
      'open-account': selectInternalTransferOpenAccountNextPage,
      'personal-info': selectInternalTransferPersonalInfoNextPage,
      'contact-info': selectInternalTransferContactInfoNextPage,
      'settlement-fund': selectInternalTransferSettlementFundNextPage,
      employment: selectInternalTransferEmploymentNextPage,
      'income-net-worth': selectInternalTransferIncomeNetWorthNextPage,
      'investment-objectives': selectInternalTransferInvestObjNextPage,
      review: selectInternalTransferReviewNextPage,
      'review-multisigner': selectInternalTransferReviewNextPage,
      'cost-basis-opt-in': selectInternalTransferCostBasisOptInNextPage,
      existing: selectInternalTransferTermsExistingNextPage,
      'existing-multisigner': selectInternalTransferTermsExistingNextPage,
      new: selectInternalTransferTermsExistingNextPage,
      'overview-alt': selectInternalTransferOverviewNextPage,
    },
    'minor-transfer': {
      'transfer-from': selectMinorTransferFromNextPage,
      'transfer-to': selectMinorTransferToNextPage,
      'personal-info': selectMinorPersonalInfoNextPage,
      'contact-info': selectMinorContactInfoNextPage,
      'settlement-fund': selectMinorSettlementFundNextPage,
      employment: selectMinorEmploymentNextPage,
      'investment-objectives': selectMinorInvestObjNextPage,
      'income-net-worth': selectMinorIncomeNetWorthNextPage,
      review: selectMinorReviewNextPage,
      existing: selectMinorTermsNextPage,
      new: selectMinorTermsNextPage,
      // open choice routes
      enroll: selectDAEnrollNextPage,
      offer: selectDAOfferNextPage,
      'tax-filing-retirement': selectTaxFilingRetirementNextPage,
      'risk-quiz-setup': selectRiskQuizSetupNextPage,
      'risk-quiz-results': selectRiskQuizResultsNextPage,
      'portfolio-match': selectPortfolioMatchNextPage,
      'multi-account-enrollment': selectMultiAccountEnrollmentNextPage,
      'investment-strategy': selectInvestmentStrategyNextPage,
    },
    'multi-inheritance': {
      // public pages
      triage: selectMultiInheritanceTriageNextPage,
      'trust-triage': selectMultiInheritanceTrustTriageNextPage,
      // secure pages
      'decedent-info': selectMultiInheritanceDecedentInfoNextPage,
      upload: selectMultiInheritanceUploadNextPage,
      'account-triage': selectMultiInheritanceAccountTriageNextPage,
      'personal-info': selectMultiInheritancePersonalInfoNextPage,
      'contact-info': selectMultiInheritanceContactInfoNextPage,
      employment: selectMultiInheritanceEmploymentNextPage,
      letter: selectMultiInheritanceLetterUploadNexPage,
      'income-net-worth': selectMultiInheritanceIncomeNetWorthNextPage,
      'investment-objectives': selectMultiInheritanceInvestObjNextPage,
      'new-account-review': selectMultiInheritanceNewAccountReviewNextPage,
      review: selectMultiInheritanceReviewNextPage,
      existing: selectMultiInheritanceTermsNextPage,
      'multi-new': selectMultiInheritanceTermsNextPage,
    },
    'inherited-ira': {
      // secure pages
      'decedent-info': selectInheritedIraDecedentInfoNextPage,
      'personal-info': selectInheritedIraPersonalInfoNextPage,
      'contact-info': selectInheritedIraContactInfoNextPage,
      'settlement-fund': selectInheritedIraSettlementFundNextPage,
      employment: selectInheritedIraEmploymentNextPage,
      letter: selectInheritedIraLetterUploadNextPage,
      'income-net-worth': selectInheritedIraIncomeNetWorthNextPage,
      'investment-objectives': selectInheritedIraInvestmentObjNextPage,
      review: selectInheritedIraReviewNextPage,
    },
    divorce: {
      upload: selectDivorceDocUploadNextPage,
      'holdings-review': selectDivorceReviewHoldingsNextPage,
       review: selectDivorceReviewNextPage,
       'divorce-existing': selectDivorceTermsNextPage,
    },
  };

  navigate$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RouterActions.Go),
        tap(({ path, query: queryParams, extras }) => {
          this.router.navigate(path, { queryParams, ...extras });
        }),
      ),
    { dispatch: false },
  );

  navigateBack$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RouterActions.Back),
        tap(() => this.location.back()),
      ),
    { dispatch: false },
  );

  navigateForward$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RouterActions.Forward),
        tap(() => this.location.forward()),
      ),
    { dispatch: false },
  );

  navigateNext$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RouterActions.Next),
      throttleTime(500),
      withLatestFrom(
        this.globalStore.pipe(select(selectRouterState)),
        this.globalStore.pipe(select(selectTriageState)),
      ),
      exhaustMap(([, routerState, triageState]) => {
        const currentPage = this.getCurrentPage(routerState);
        const flow = urlMapper[triageState.flowType];
        return of(currentPage).pipe(
          withLatestFrom(this.globalStore.pipe(select(this.routeMap[flow][currentPage]))),
          exhaustMap(([, nextPage]) => {
            return [RouterActions.Go({ path: [nextPage] })];
          }),
        );
      }),
    ),
  );

  navigateExternal$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RouterActions.External),
        map((payload) => {
          this.winRef.nativeWindow.location.href = payload.url;
        }),
      ),
    { dispatch: false },
  );

  getCurrentPage(routerState: RouterStateUrl): string {
    /**
     * We need this logic because we have a route with a parameter at the moment and we might have others in the future.
     * However, this assumes that the params are at the end of the route configuration. If we will have any route with params
     * In the middle of the route, we will need to revisit this logic.
     */
    const currentDomain = routerState.url.split('?')[0].split('/');
    const lastIndex = currentDomain.length - 1;
    const paramsCount = Object.keys(routerState.params).length;
    return currentDomain[lastIndex - paramsCount];
  }
}
